import type { FileSystemTree } from '@webcontainer/api'
import type { ProjectVersion } from '@/lib/types'
import { useState } from 'react'

type SaveMode = 'fast' | 'legacy'

type SaveResponse = {
  versions: Record<string, ProjectVersion>
  file_system_tree_index: number
  build?: {
    id: string
    status: 'building' | 'ready' | 'failed' | 'superseded'
    version: number
    requestedAt: string
  }
}

export const useSaveChanges = (timeout = 10 * 1000) => {
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(false)
  const save = async (
    id: string,
    tree: FileSystemTree,
    mode: SaveMode = 'fast',
  ): Promise<SaveResponse | undefined> => {
    setLoading(true)
    try {
      const endpoint = mode === 'legacy' ? 'editor' : 'save-fast'
      const resp = await fetch(`/api/me/project/${id}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileSystemTree: tree,
        }),
      })
      if (resp.ok) {
        return ((await resp.json()) as { data: SaveResponse }).data
      }

      // Fallback to legacy route if fast path is unavailable
      if (mode === 'fast' && resp.status === 404) {
        return await save(id, tree, 'legacy')
      }
      throw new Error('Error saving changes')
    } catch (e) {
      setError(e as Error)
      setTimeout(() => setError(null), timeout)
    } finally {
      setLoading(false)
    }
  }

  return {
    save,
    error,
    loading,
    setError: (e: Error, timeout = 10 * 1000) => {
      setError(e)
      setTimeout(() => setError(null), timeout)
    },
  }
}
